/* eslint-disable import/prefer-default-export */
// Turns the API's raw confirm/run validation errors (pydantic-style, e.g.
// `demographics: Input should be a valid dictionary`) into plain-language copy and
// the Step 2 section that holds the offending field, so the UI can jump there.

const FALLBACK_MESSAGE = "We couldn't submit the audit. Please review your details and try again.";

// Correction-body field (first path segment) → user-facing label + confirm section.
const FIELD_INFO = {
  subjectName: { label: 'Subject / Company', section: 1 },
  subjectUrl: { label: 'Product URL', section: 1 },
  subjectType: { label: 'Subject type', section: 1 },
  subjectSummary: { label: 'Subject summary', section: 1 },
  objective: { label: 'Objective', section: 1 },
  currency: { label: 'Currency', section: 2 },
  totals: { label: 'Total spend', section: 2 },
  timeframe: { label: 'Campaign dates', section: 2 },
  influencers: { label: 'Influencers', section: 3 },
};

// Influencer sub-field → label.
const INFLUENCER_FIELD_LABELS = {
  handle: 'handle',
  platform: 'platform',
  url: 'profile URL',
  spend: 'spend',
  spendCurrency: 'spend currency',
  posts: 'number of posts',
  outcomes: 'outcomes',
};

/** Raw pydantic / Laravel message → a short plain-English predicate. */
const humanizeProblem = (raw = '') => {
  const text = String(raw).toLowerCase();
  if (/field required|is required|missing/.test(text)) return 'is required';
  if (/valid (number|integer|float)|must be a number|numeric/.test(text)) return 'must be a number';
  if (/valid url/.test(text)) return 'must be a valid URL';
  if (/valid date|date format/.test(text)) return 'must be a valid date';
  return 'has an invalid value';
};

/** `influencers.0.posts` → "Influencer @handle — number of posts". */
const describeField = (key, values) => {
  const [root, index, sub] = String(key).split('.');
  const info = FIELD_INFO[root];
  if (!info) return null;
  if (root === 'influencers' && index != null && /^\d+$/.test(index)) {
    const handle = values?.influencers?.[Number(index)]?.handle;
    const who = handle
      ? `Influencer @${String(handle).replace(/^@/, '')}`
      : `Influencer #${Number(index) + 1}`;
    const what = INFLUENCER_FIELD_LABELS[sub];
    return { label: what ? `${who} — ${what}` : who, section: info.section };
  }
  return info;
};

/**
 * @param {object} resp  failed API envelope ({ errors?, error?, message? })
 * @param {object} values the confirm form values (to name influencers by handle)
 * @returns {{ message: string, section: number|null }}
 */
export const friendlyConfirmError = (resp, values) => {
  // The field error arrives either in the `errors` map or as an `error` string shaped
  // like "demographics: Input should be a valid dictionary".
  let key = null;
  let raw = null;
  const errors = resp?.errors;
  if (errors && typeof errors === 'object' && Object.keys(errors).length) {
    [key] = Object.keys(errors);
    raw = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
  } else {
    const match = /^([\w.]+):\s*(.+)$/.exec(String(resp?.error || ''));
    if (match) [, key, raw] = match;
  }

  const field = key ? describeField(key, values) : null;
  if (field) {
    return {
      message: `We couldn't save your audit: ${field.label} ${humanizeProblem(
        raw
      )}. Please review it and try again.`,
      section: field.section,
    };
  }

  // A readable sentence from the API (e.g. "Audit must be confirmed…") is fine as is;
  // anything that looks like a raw `field: validator message` falls back to generic copy.
  const text = resp?.error || resp?.message;
  if (
    text &&
    !/^[\w.]+:\s/.test(text) &&
    !/^(cannot complete request|validation failed)$/i.test(text)
  ) {
    return { message: text, section: null };
  }
  return { message: FALLBACK_MESSAGE, section: null };
};
